import { fromInteger, toInteger } from '@repo/money'
import type { Channel } from 'phoenix'
import { useCallback } from 'react'
import { z } from 'zod'
import type { CalculatePayoutDTO } from 'src/actions/types'
import type { BetDetails, BetType } from 'src/types/domain/BetDetails'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import { isAuthenticatedPlayer, type Player } from 'src/types/domain/Player'
import { promisifyPush } from 'src/utils/promisifyPush'

type Params = Readonly<{
  playerChannel: Channel | undefined
  player: Player
  game: MarbleGame
}>

const payoutCalculationSchema = z.object({
  probability: z.number(),
  basePayout: z.number(),
  totalPayout: z.number(),
  baseMultiplier: z.number(),
  totalMultiplier: z.number(),
  // Streak multiplier info (optional - only present when player has a streak)
  streakBoostPercent: z.number().optional(),
  streakType: z.string().optional(),
  streakCount: z.number().optional(),
  // Level multiplier info (optional - only present when level system is enabled)
  levelBoostPercent: z.number().optional(),
  currentLevel: z.number().optional(),
  currentXP: z.number().optional()
})

interface CalculationResult {
  payout: number
  multiplier: number
  probability: number
  bonusPayout?: number
  bonusMultiplier?: number
  // Streak info
  streakBoostPercent?: number
  streakType?: 'win' | 'loss'
  streakCount?: number
  // Level info
  levelBoostPercent?: number
  currentLevel?: number
}

export const usePayoutCalculation = ({ playerChannel, player, game }: Params) => {
  const calculatePayout = useCallback(
    async ({
      betAmount,
      selectedMarbles,
      betType,
      inventoryItemIds = [],
      roundId
    }: {
      betAmount: string
      selectedMarbles: Marble[]
      betType: BetType
      inventoryItemIds?: string[]
      roundId?: string // Optional: when provided, lightning round boost is applied if applicable
    }): Promise<CalculationResult | undefined> => {
      if (!playerChannel || playerChannel.state !== 'joined') {
        console.warn(
          'Cannot calculate payout. Player channel not ready, state:',
          playerChannel?.state || 'undefined'
        )
        // Don't capture exception for temporary unavailability - this is expected during reconnections
        return
      }

      if (!isAuthenticatedPlayer(player)) {
        console.error('Player not authenticated')
        return
      }

      if (!betAmount || selectedMarbles.length === 0) {
        console.error('Missing bet input')
        return
      }

      try {
        const payload: CalculatePayoutDTO = {
          gameId: game.id,
          ...(roundId ? { roundId } : {}), // Only include roundId if provided (for lightning round boost)
          betAmount: toInteger(betAmount, player.balance.currency),
          betDetails: {
            type: betType,
            selectedItems: selectedMarbles.map((m) => m.uuid),
            inventoryItemIds
          } as BetDetails
        }

        const result = await promisifyPush({
          channel: playerChannel,
          eventName: 'client:bet:calculate-payout',
          payload
        })

        const parsed = payoutCalculationSchema.safeParse(result)

        if (!parsed.success) {
          console.error('Validation failed:', parsed.error)
          return
        }

        const {
          totalPayout,
          basePayout,
          totalMultiplier,
          baseMultiplier,
          probability,
          streakBoostPercent,
          streakType,
          streakCount,
          levelBoostPercent,
          currentLevel
        } = parsed.data

        const bonusPayoutInt = Math.max(0, totalPayout - basePayout)
        const bonusMultiplierRaw = Math.max(0, totalMultiplier - baseMultiplier)

        const payout = fromInteger(totalPayout, player.balance.currency)
        const bonusPayout = fromInteger(bonusPayoutInt, player.balance.currency)

        // Build result with conditional spreads so bonus*, streak*, and level* are truly optional
        const out: CalculationResult = {
          payout,
          multiplier: totalMultiplier,
          probability,
          ...(bonusPayout > 0 ? { bonusPayout } : {}),
          ...(bonusMultiplierRaw > 0 ? { bonusMultiplier: bonusMultiplierRaw } : {}),
          ...(streakBoostPercent && streakBoostPercent > 0
            ? {
                streakBoostPercent,
                streakType: streakType as 'win' | 'loss',
                streakCount
              }
            : {}),
          ...(levelBoostPercent && levelBoostPercent > 0
            ? {
                levelBoostPercent,
                currentLevel
              }
            : {})
        }

        return out
      } catch (err) {
        console.error('Payout calculation failed:', err)
      }
    },
    [playerChannel, player, game.id]
  )

  return calculatePayout
}
